import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ChangePasswordRequest,
  LoginRequest,
  Permission,
  SessionBootstrap,
} from '@blesk/contracts';
import { apiGet, apiPost } from './api';

/**
 * §15 ТЗ, спека 85-auth-vibecode.
 *
 * `GET /api/session/bootstrap` — единственный источник правды о режиме, правах
 * и доступных объектах. SPA не определяет режим по адресу страницы и не выводит
 * права из роли: и то и другое приходит с сервера одним ответом.
 */
export const SESSION_QUERY_KEY = ['session', 'bootstrap'] as const;

export const sessionQuery = queryOptions({
  queryKey: SESSION_QUERY_KEY,
  queryFn: () => apiGet<SessionBootstrap>('/api/session/bootstrap'),
  // Режим и права меняются редко; перечитываем только по явному поводу —
  // вход, выход, смена пароля, ответ 401 от любого запроса.
  staleTime: Infinity,
  retry: 1,
});

export function useSession(): SessionBootstrap {
  const { data } = useQuery(sessionQuery);
  if (!data) {
    // До отрисовки защищённых экранов маршрут уже дождался ответа в beforeLoad
    throw new Error('Сессия не загружена: экран отрисован вне защищённого маршрута');
  }
  return data;
}

/** Право есть у роли. Проверка на сервере от этого не отменяется (§3). */
export function hasPermission(
  session: SessionBootstrap,
  permission: Permission | undefined,
): boolean {
  if (!permission) return true;
  return session.permissions.includes(permission);
}

export function useHasPermission(permission: Permission | undefined): boolean {
  return hasPermission(useSession(), permission);
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginRequest) => apiPost<{ ok: true }>('/api/auth/login', body),
    // refetchType: 'all' — на экране входа подписчиков у запроса нет,
    // а пометить его устаревшим мало: охрана маршрута прочитает кэш
    // и снова решит, что человек не вошёл
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY, refetchType: 'all' }),
  });
}

/**
 * Выход. Кэш вкладки обнуляется — чужих данных в памяти остаться не должно, —
 * но порядок здесь важнее самого обнуления.
 *
 * Запись о сессии не стирается, а заменяется анонимной, и делается это до
 * навигации. Стёртая запись означала бы для `useSession` «сессия не загружена»,
 * а `AppShell` в этот момент ещё отрисован: он падал бы с ошибкой, и человек
 * вместо страницы входа получал аварийный экран. Прежний же ответ, оставленный
 * в кэше, охрана `/login` прочитала бы как «уже вошёл» и вернула бы человека
 * внутрь — выход не срабатывал вовсе.
 */
export function useLogout() {
  const queryClient = useQueryClient();
  // Отзыв сессии на сервере мог и не пройти — например, она истекла раньше.
  // Для вкладки исход один и тот же: данные забываются, человек идёт на вход.
  const forget = () => {
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0],
    });
    queryClient.setQueryData<SessionBootstrap>(SESSION_QUERY_KEY, (previous) =>
      previous
        ? {
            ...previous,
            authenticated: false,
            employee: null,
            allowedProjects: null,
            permissions: [],
          }
        : previous,
    );
  };

  return useMutation({
    mutationFn: () => apiPost<void>('/api/auth/logout'),
    onSuccess: forget,
    onError: forget,
  });
}

/** §15.3: смена начального пароля обязательна и не пропускается. */
export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ChangePasswordRequest) => apiPost<void>('/api/auth/password', body),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY, refetchType: 'all' }),
  });
}
